import React, { useRef, useState } from "react";

export type FieldName = "firstName" | "lastName" | "phone" | "email" | "zip" | "details";
type Values = Record<FieldName, string>;
export type FieldErrors = Partial<Record<FieldName, string>>;

export const fieldOrder: { name: FieldName; label: string }[] = [
  { name: "firstName", label: "First name" },
  { name: "lastName", label: "Last name" },
  { name: "phone", label: "Phone number" },
  { name: "email", label: "Email" },
  { name: "zip", label: "ZIP code" },
  { name: "details", label: "Crash details" },
];

const initialValues: Values = { firstName: "", lastName: "", phone: "", email: "", zip: "", details: "" };

function validate(v: Values): FieldErrors {
  const errors: FieldErrors = {};
  if (!v.firstName.trim()) errors.firstName = "Enter your first name.";
  if (!v.lastName.trim()) errors.lastName = "Enter your last name.";

  const digits = v.phone.replace(/\D/g, "");
  if (!digits) errors.phone = "Enter a phone number so the firm can reach you.";
  else if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))))
    errors.phone = "Enter a 10-digit phone number, for example 248-555-0123.";

  if (!v.email.trim()) errors.email = "Enter your email address.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim()))
    errors.email = "Enter an email address in the format name@example.com.";

  if (v.zip.trim() && !/^\d{5}$/.test(v.zip.trim())) errors.zip = "Enter a 5-digit ZIP code, or leave it blank.";
  return errors;
}

export function useCaseReviewForm() {
  const [values, setValues] = useState<Values>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const handleChange =
    (name: FieldName) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = { ...values, [name]: e.target.value };
      setValues(next);
      if (attempted) setErrors(validate(next));
    };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Design preview: never transmit data anywhere.
    e.preventDefault();
    setAttempted(true);
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      if (nextErrors.zip) setShowOptional(true);
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    setPreviewSubmitted(true);
    requestAnimationFrame(() => statusRef.current?.focus());
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setAttempted(false);
    setPreviewSubmitted(false);
    setShowOptional(false);
  };

  const errorList = fieldOrder.filter((f) => errors[f.name]).map((f) => ({ ...f, message: errors[f.name] as string }));

  return {
    values,
    errors,
    errorList,
    previewSubmitted,
    showOptional,
    setShowOptional,
    summaryRef,
    statusRef,
    handleChange,
    handleSubmit,
    reset,
  };
}
